import type { Permission } from "@plannello/shared";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { api } from "./api";
import { useMe } from "./queries";

/** `GET /api/v1/me/permissions`: effective permissions per scope (token scopes applied). */
export type MyPermissions = {
  instance: Permission[];
  spaces: Record<string, Permission[]>;
  products: Record<string, Permission[]>;
};

export const permissionsQuery = queryOptions({
  queryKey: ["me", "permissions"],
  queryFn: ({ signal }) => api.get<MyPermissions>("/api/v1/me/permissions", signal),
  staleTime: 60_000,
});

/** Permissions that open at least part of Administration. */
export const ADMIN_PERMISSIONS: Permission[] = [
  "instance.admin",
  "users.manage",
  "roles.manage",
  "audit.read",
  "schema.manage",
];

export function useMyPermissions() {
  return useQuery(permissionsQuery);
}

/** True for owners and for anyone holding at least one instance level administrative permission. */
export function useCanAdmin(): boolean {
  const me = useMe().data;
  const perms = useMyPermissions().data;
  if (me?.isOwner) return true;
  return !!perms && ADMIN_PERMISSIONS.some((p) => perms.instance.includes(p));
}
